import {
  formatCount,
  formatDecimal,
  formatPercent,
  formatResponseTime,
  formatTenureMonths,
  formatTrendChange,
} from "@/components/mentor-performance/metric-formatters";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  MentorMetricsSummary,
  MentorMetricsSummaryMetric,
} from "@/lib/api/mentor-metrics-dashboard";
import { cn } from "@/lib/utils";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { IconButton } from "@somethings-ui/web";
import { Loader2 } from "lucide-react";

interface SummaryStripProps {
  summary: MentorMetricsSummary | undefined;
  isLoading: boolean;
  isRefreshing?: boolean;
  isError: boolean;
}

const METRIC_DETAILS: {
  key: keyof Pick<
    MentorMetricsSummary,
    | "totalMentors"
    | "averageTenureMonths"
    | "averageAppealScore"
    | "averageActiveCaseload"
    | "averageResponseTimeSeconds"
    | "averageD3RetentionRate"
  >;
  label: string;
  tooltip: string | null;
  formatValue: (metric: MentorMetricsSummaryMetric) => string;
  formatChange: (metric: MentorMetricsSummaryMetric) => string;
}[] = [
  {
    key: "totalMentors",
    label: "Total mentors",
    tooltip: "All mentors in this cohort.",
    formatValue: (metric) => formatCount(metric.value),
    formatChange: (metric) =>
      formatTrendChange({
        absoluteChange: metric.trend.absoluteChange,
        direction: metric.trend.direction,
        format: formatCount,
      }),
  },
  {
    key: "averageTenureMonths",
    label: "Avg tenure",
    tooltip:
      "The mean time on the platform across all mentors in this cohort, rounded to the nearest month.",
    formatValue: (metric) =>
      metric.value === null ? "—" : formatTenureMonths(metric.value),
    formatChange: (metric) =>
      formatTrendChange({
        absoluteChange: metric.trend.absoluteChange,
        direction: metric.trend.direction,
        format: (value) => formatDecimal(value),
      }),
  },
  {
    key: "averageAppealScore",
    label: "Avg profile score",
    tooltip:
      "The appeal of a mentor's matching profile, based on how often it was selected after being viewed. Uses the last 30 days when the current month is selected; otherwise the full selected month.",
    formatValue: (metric) => formatDecimal(metric.value),
    formatChange: (metric) =>
      formatTrendChange({
        absoluteChange: metric.trend.absoluteChange,
        direction: metric.trend.direction,
        format: (value) => formatDecimal(value),
      }),
  },
  {
    key: "averageActiveCaseload",
    label: "Avg caseload",
    tooltip:
      "The average number of engaged teens compared with the average total caseload for this period.",
    formatValue: (metric) => formatDecimal(metric.value),
    formatChange: (metric) =>
      formatTrendChange({
        absoluteChange: metric.trend.absoluteChange,
        direction: metric.trend.direction,
        format: (value) => formatDecimal(value),
      }),
  },
  {
    key: "averageResponseTimeSeconds",
    label: "Median response time",
    tooltip:
      "The typical time a mentor takes to respond to a teen while on shift, taken as a median so one slow reply does not skew it. Uses the last 30 days when the current month is selected; otherwise the full selected month.",
    formatValue: (metric) => formatResponseTime(metric.value),
    formatChange: (metric) =>
      formatTrendChange({
        absoluteChange: metric.trend.absoluteChange,
        direction: metric.trend.direction,
        format: (value) => formatResponseTime(value),
      }),
  },
  {
    key: "averageD3RetentionRate",
    label: "Avg D3 retention rate",
    tooltip:
      "The percentage of teens who have engaged with the mentor after 3 days on the platform, averaged across mentors with enough data. Uses the last 30 days when the current month is selected; otherwise the full selected month.",
    formatValue: (metric) => formatPercent(metric.value),
    formatChange: (metric) =>
      formatTrendChange({
        absoluteChange: metric.trend.absoluteChange,
        direction: metric.trend.direction,
        format: (value) => formatPercent(value),
      }),
  },
];

const trendStyles = {
  up: "text-text-secondary",
  down: "text-text-secondary",
  unchanged: "text-text-secondary",
  unavailable: "text-text-tertiary",
} as const;

function SummaryStripSkeleton() {
  return (
    <div
      aria-busy
      aria-label="Loading summary metrics"
      className="grid min-h-32 w-full grid-cols-2 overflow-hidden rounded-md border border-surfaces-z5 bg-surfaces-z0 md:grid-cols-3 xl:grid-cols-[254px_repeat(5,minmax(0,1fr))_84px]"
    >
      {METRIC_DETAILS.map(({ key }, index) => (
        <div
          key={key}
          className={cn(
            "flex min-w-0 flex-col justify-center gap-3 px-6 py-4",
            index < METRIC_DETAILS.length - 1 &&
              "border-b border-r border-surfaces-z5 xl:border-b-0",
            index === METRIC_DETAILS.length - 1 && "xl:col-span-2",
          )}
        >
          <Skeleton className="h-3 w-24" />
          <Skeleton className="h-8 w-16" />
          <Skeleton className="h-3 w-14" />
        </div>
      ))}
    </div>
  );
}

function CaseloadValue({
  engaged,
  total,
}: {
  engaged: number | null;
  total: number | null;
}) {
  if (engaged === null || total === null) return <>—</>;

  return (
    <>
      {formatDecimal(engaged, 0)}{" "}
      <span className="text-black/45">/ {formatDecimal(total, 0)}</span>
    </>
  );
}

export function SummaryStrip({
  summary,
  isLoading,
  isRefreshing = false,
  isError,
}: SummaryStripProps) {
  if (isError) {
    return (
      <div className="flex min-h-32 w-full items-center justify-center rounded-md border border-surfaces-z5 bg-surfaces-z0 text-body-2 text-text-secondary">
        Failed to load summary metrics
      </div>
    );
  }

  if (isLoading || !summary) {
    return <SummaryStripSkeleton />;
  }

  return (
    <div
      aria-busy={isRefreshing}
      className={cn(
        "relative grid min-h-32 w-full grid-cols-2 overflow-hidden rounded-md border border-surfaces-z5 bg-surfaces-z0 transition-opacity md:grid-cols-3 xl:grid-cols-[254px_repeat(5,minmax(0,1fr))_84px]",
        isRefreshing && "opacity-60",
      )}
    >
      {isRefreshing && (
        <div className="pointer-events-none absolute right-3 top-3 z-10 flex items-center gap-2 rounded-md bg-surfaces-z0/90 px-2 py-1 text-body-3 text-text-secondary shadow-sm">
          <Loader2 className="size-3.5 animate-spin" aria-hidden />
          Updating…
        </div>
      )}
      {METRIC_DETAILS.map(
        ({ key, label, tooltip, formatValue, formatChange }, index) => {
          const metric = summary[key];
          const profileScoreUnavailable =
            key === "averageAppealScore" && !summary.appealScoreAvailable;
          const trendDirection = profileScoreUnavailable
            ? "unavailable"
            : metric.trend.direction;
          const arrow = profileScoreUnavailable
            ? null
            : metric.trend.direction === "up"
              ? "↑"
              : metric.trend.direction === "down"
                ? "↓"
                : null;

          return (
            <div
              key={key}
              className={cn(
                "flex min-w-0 flex-col justify-center gap-3 px-6 py-4",
                index < METRIC_DETAILS.length - 1 &&
                  "border-b border-r border-surfaces-z5 xl:border-b-0",
                index === METRIC_DETAILS.length - 1 && "xl:col-span-2",
              )}
            >
              <div className="flex items-center gap-1">
                <span className="whitespace-nowrap text-body-3 uppercase tracking-[0.02em] text-black/50">
                  {label}
                </span>
                {tooltip && (
                  <TooltipProvider delayDuration={100}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <IconButton
                          size="sm"
                          shape="circle"
                          variant="ghost-gray"
                          aria-label={`About ${label}`}
                          className="!h-4 !w-4 !p-0 text-black/40 hover:bg-transparent"
                          icon={<InformationCircleIcon className="size-3" />}
                        />
                      </TooltipTrigger>
                      <TooltipContent
                        side="top"
                        className="max-w-64 rounded-lg bg-black px-3 py-2 text-xs leading-5 text-white"
                      >
                        {tooltip}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>

              <p className="font-heading text-header-2 text-black">
                {profileScoreUnavailable ? (
                  "Unavailable"
                ) : key === "averageActiveCaseload" ? (
                  <CaseloadValue
                    engaged={summary.averageEngagedTeens}
                    total={metric.value}
                  />
                ) : (
                  formatValue(metric)
                )}
              </p>

              <p
                className={cn(
                  "text-body-3",
                  trendStyles[trendDirection],
                  trendDirection !== "unchanged" && "opacity-75",
                )}
              >
                {profileScoreUnavailable ? (
                  "—"
                ) : (
                  <>
                    {arrow && <span className="mr-1">{arrow}</span>}
                    {formatChange(metric)}
                  </>
                )}
              </p>
            </div>
          );
        },
      )}
    </div>
  );
}
